import React, { useState } from 'react';
import { useProcesses } from '../context/ProcessesContext';
import ProcessTrainingWindow from '../components/training/ProcessTrainingWindow';
import '../styles/dashboard-pages.css';

const ProcessesPage: React.FC = () => {
  const { processes, clearAllProcesses } = useProcesses();
  const [selectedProcessId, setSelectedProcessId] = useState<string | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return '#22c55e';
      case 'completed':
        return '#3b82f6';
      case 'failed':
        return '#ef4444';
      case 'paused':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return '▶️';
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      case 'paused':
        return '⏸️';
      default:
        return '⏹️';
    }
  };

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Training Processes</h1>
        <p>Monitor and manage all your active training jobs</p>
      </div>

      <div className="page-sections">
        <section className="section-card">
          <div className="section-header">
            <h2>Active Processes</h2>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                {processes.filter(p => p.status === 'running').length} running
              </div>
              {processes.length > 0 && (
                <button
                  onClick={() => {
                    if (window.confirm('Clear all processes? This will remove all process history.')) {
                      clearAllProcesses();
                    }
                  }}
                  style={{
                    padding: '4px 12px',
                    fontSize: '12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#f87171',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                  title="Clear all processes"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          <div className="processes-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '20px' 
          }}>
            {processes.length === 0 ? (
              <div style={{ 
                gridColumn: '1 / -1', 
                textAlign: 'center', 
                padding: '48px',
                color: '#9ca3af',
                fontSize: '16px'
              }}>
                No active training processes. Start a training job to see it here.
              </div>
            ) : (
              processes.map((process) => (
              <div
                key={process.id}
                className="process-card-item"
                onClick={() => setSelectedProcessId(process.id)}
                style={{
                  padding: '24px',
                  background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.7) 0%, rgba(30, 41, 59, 0.7) 100%)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: '16px',
                  border: '1px solid rgba(148, 163, 184, 0.15)',
                  boxShadow: '0 15px 40px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 600, 
                      color: '#e4e4e7', 
                      marginBottom: '8px' 
                    }}>
                      {process.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
                      {process.config?.description || 'No description'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      {process.training_type === 'runpod' ? 'RunPod' : 'Local'} Training
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '24px',
                    padding: '8px',
                    background: `rgba(${getStatusColor(process.status).slice(1).match(/.{2}/g)?.map(x => parseInt(x, 16)).join(', ')}, 0.1)`,
                    borderRadius: '8px',
                  }}>
                    {getStatusIcon(process.status)}
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: '8px',
                    fontSize: '12px',
                    color: '#9ca3af'
                  }}>
                    <span>Progress</span>
                    <span>{process.progress}%</span>
                  </div>
                  <div style={{
                    height: '8px',
                    background: 'rgba(99, 102, 241, 0.1)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${process.progress}%`,
                      background: `linear-gradient(90deg, ${getStatusColor(process.status)} 0%, ${getStatusColor(process.status)}dd 100%)`,
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  fontSize: '12px',
                  color: '#9ca3af',
                  marginBottom: '16px',
                }}>
                  <span>Epoch {process.epoch} / {process.totalEpochs}</span>
                  <span>{process.startTime}</span>
                </div>
                
                {process.train_loss > 0 && (
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#6b7280', 
                    marginBottom: '8px',
                    display: 'flex',
                    gap: '12px'
                  }}>
                    <span>Train Loss: {process.train_loss.toFixed(4)}</span>
                    <span>Val Loss: {process.val_loss.toFixed(4)}</span>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                  {process.status === 'running' && (
                    <>
                      <button className="action-btn secondary small">Pause</button>
                      <button className="action-btn secondary small" style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#f87171' }}>Stop</button>
                    </>
                  )}
                  {process.status === 'paused' && (
                    <>
                      <button className="action-btn primary small">Resume</button>
                      <button className="action-btn secondary small" style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#f87171' }}>Stop</button>
                    </>
                  )}
                  {process.status === 'completed' && (
                    <button className="action-btn primary small">View Results</button>
                  )}
                  <button 
                    className="icon-btn" 
                    title="View Details"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProcessId(process.id);
                    }}
                  >
                    👁️
                  </button>
                </div>
              </div>
              ))
            )}
          </div>
        </section>
      </div>
      
      {selectedProcessId && (
        <ProcessTrainingWindow
          processId={selectedProcessId}
          onClose={() => setSelectedProcessId(null)}
        />
      )}
    </div>
  );
};

export default ProcessesPage;

